import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { APPS } from "../data/apps";
import { AppIcon } from "./AppIcon";

const SLIDES = [
  { img: "https://customer-assets-cm19k8pv.emergentagent.net/job_tirera-apps/artifacts/w70zxaqo_ce249ba3-b3b4-4b8f-be6c-11615b3606ed.jpeg", city: "Bamako, Samanko" },
  { img: "https://static.prod-images.emergentagent.com/jobs/4bc8c93c-fa86-469a-a49a-36eba45f3ee2/images/17bcfb732e45a749993a2fff80027a7b0d589710910b6c58619f795f8c9c4493.jpeg", city: "Bamako" },
  { img: "https://static.prod-images.emergentagent.com/jobs/4bc8c93c-fa86-469a-a49a-36eba45f3ee2/images/98c5257509ea6263ec0c234937bd00457a82cab43eb6a01ba3b79eae5973b8de.jpeg", city: "Dakar" },
];

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.11, ease: [0.16, 1, 0.3, 1] },
  }),
};

const HEADLINE = ["5 activités, 1 vision."];

export const Hero = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % SLIDES.length), 6500);
    return () => clearInterval(id);
  }, []);

  return (
    <section data-testid="hero-section" className="relative min-h-[100svh] w-full overflow-hidden flex items-center">
      {/* Background carousel */}
      <div className="absolute inset-0">
        <AnimatePresence>
          <motion.div
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: "easeInOut" }}
            className="absolute inset-0"
          >
            <motion.img
              src={SLIDES[index].img}
              alt={`Avenue moderne — ${SLIDES[index].city}`}
              initial={{ scale: 1.04 }}
              animate={{ scale: 1.1 }}
              transition={{ duration: 12, ease: "linear" }}
              className="h-full w-full object-cover"
              style={{ willChange: "transform" }}
            />
          </motion.div>
        </AnimatePresence>
        {/* Dark navy veil for readability */}
        <div className="absolute inset-0 bg-navy/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/45 to-navy/35" />
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8 pt-28 pb-24 lg:pt-32">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 backdrop-blur-md px-4 py-1.5 text-xs font-medium text-white/90"
        >
          <span className="h-2 w-2 rounded-full bg-africain animate-pulse" />
          TIRERA GROUP SARL — Bamako, Mali
        </motion.div>

        <h1 className="mt-6 font-display font-semibold tracking-tight text-white text-3xl sm:text-5xl lg:text-6xl leading-[1.08] max-w-4xl drop-shadow-sm">
          {HEADLINE.map((l, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                {l}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-3 max-w-2xl text-xl sm:text-2xl lg:text-3xl font-semibold leading-snug text-white/50"
        >
          Construire l'avenir de <span className="text-white">l'Afrique</span> grâce à la{" "}
          <span className="text-white">mobilité, l'immobilier, la construction, le commerce</span> et les{" "}
          <span className="text-white">services numériques.</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.82 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/applications"
            data-testid="hero-cta-apps"
            className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-navy hover:bg-white/90 transition-colors"
          >
            Nos activités
            <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
          </Link>
          <a
            href="#dashboard"
            data-testid="hero-cta-stats"
            className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/5 backdrop-blur-md px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/15 transition-colors"
          >
            Statistiques en direct
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1 }}
          className="mt-14 grid grid-cols-2 gap-3 sm:gap-4 max-w-2xl"
        >
          {APPS.map((a, i) => {
            const featured = i === 0;
            if (featured) {
              return (
                <Link
                  key={a.slug}
                  to={`/applications/${a.slug}`}
                  data-testid={`hero-app-chip-${a.slug}`}
                  className="group row-span-2 flex flex-col justify-between rounded-2xl border border-white/20 bg-white/10 backdrop-blur-md p-4 hover:bg-white/20 hover:-translate-y-1 transition-all duration-300"
                  style={{ boxShadow: `inset 0 0 0 1px ${a.accent}55` }}
                >
                  <div className="flex items-start justify-between">
                    <AppIcon app={a} variant="solid" iconSize={30} containerClass="h-16 w-16 shrink-0 rounded-2xl shadow-lg" />
                    <span className="rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-semibold text-white">Activité principale</span>
                  </div>
                  <div className="mt-4">
                    <span className="block font-display font-bold text-white text-lg leading-tight">{a.name}</span>
                    <span className="mt-0.5 block text-xs font-semibold" style={{ color: a.accent }}>{a.category}</span>
                    <span className="mt-2 flex items-center gap-1 text-xs text-white/70">Découvrir <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" /></span>
                  </div>
                </Link>
              );
            }
            return (
              <Link
                key={a.slug}
                to={`/applications/${a.slug}`}
                data-testid={`hero-app-chip-${a.slug}`}
                className="group flex items-center gap-3 rounded-2xl border border-white/15 bg-white/10 backdrop-blur-md p-3.5 hover:bg-white/20 hover:-translate-y-1 transition-all duration-300"
              >
                <AppIcon app={a} variant="solid" iconSize={18} containerClass="h-10 w-10 shrink-0 rounded-xl" />
                <span className="min-w-0">
                  <span className="block font-display font-semibold text-white text-sm truncate">{a.name}</span>
                  <span className="block text-xs text-white/60 truncate">{a.category}</span>
                </span>
              </Link>
            );
          })}
        </motion.div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-7 left-0 right-0 z-10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white/80 text-sm font-medium">
            <MapPin size={15} className="text-africain" />
            <AnimatePresence mode="wait">
              <motion.span
                key={SLIDES[index].city}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.4 }}
              >
                {SLIDES[index].city}
              </motion.span>
            </AnimatePresence>
          </div>
          <div className="flex items-center gap-2">
            {SLIDES.map((s, i) => (
              <button
                key={s.city}
                data-testid={`hero-slide-dot-${i}`}
                aria-label={`Voir ${s.city}`}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  i === index ? "w-8 bg-white" : "w-4 bg-white/40 hover:bg-white/60"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
